import { Button } from "react-bootstrap";
import "./Attachment.css";
import { useContext, useEffect, useRef, useState } from "react";
import cancelIcon from './../../assets/icons/cancel.svg';
import Helper from "../../util/helper";
import { FormContext } from "../../contexts/FormContext";
import { RenderScrollContext } from "../../contexts/RenderScrollContext";

import { CAMERA, FILE } from "../../constants/GeneralConstants";
import { AttachmentIcon } from "../SvgComponents/AttachmentIcon/AttachmentIcon";
import { useTranslation } from "react-i18next";
import { isMobile } from 'react-device-detect';
import { ShowErrorModal } from "../Modals/ShowErrorModal/ShowErrorModal";
import { BatchContext } from "../../contexts/BatchContext";
import { ATTACHMENT_FILE_SIZE_LIMIT_IN_BYTES, ATTACHMENT_FILE_SIZE_LIMIT_IN_MB } from "../../util/constants";

export const Attachment = ({ isAdvancedForm, componentData, onUpdateValue, isHorizontal }) => {
  const titleValue = isAdvancedForm ? componentData.title.disableprintpdf ? '' : componentData.title.text : componentData.title;

  const inputFileRef = useRef();
  const [uploadedFile, setFile] = useState(null);
  const { isHTML, checkDisabled, formTransform, getScreenDimensions } = useContext(FormContext);
  const { calculateWidthPercentageOfPage, getPageWidth, getPageHeight } = useContext(RenderScrollContext);

  const { isBatchMultisignature } = useContext(BatchContext);
  const [error, setError] = useState(null);

  const isDisabled = checkDisabled(componentData);
  const hasError = componentData.error;
  const { t } = useTranslation();
  const [contentType, setContentType] = useState('image/*');
  const [infoContainerStyle, setInfoContainerStyle] = useState({});
  const imageRef = useRef(null);

  // Check if title is a string and not empty
  const shouldAddButtonMargin = isAdvancedForm && (titleValue && titleValue.split('').some(char => char !== ' '));
  const buttonMarginTop = isMobile ? 5 : 15;

  useEffect(() => {
    if (componentData.response.fileinbase64 === null) {
      setFile(null);
    }

    if (componentData.response.contenttype && componentData.response.contenttype.toUpperCase() === 'PDF') {
      setContentType('application/pdf');
    }

    const baseStyle = isAdvancedForm ? getResponseStyles() : {};
    const baseTopValue = parseInt(baseStyle.top?.replace('px', '') || 0, 10);
    const updateInfoStyle = () => {
      const imageHeight = imageRef.current?.offsetHeight || 0;
      const infoContainerStyle = {
        ...baseStyle,
        height: 'auto',
        top: `${baseTopValue + imageHeight}px`,
      };
      setInfoContainerStyle(infoContainerStyle);
    };

    updateInfoStyle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formTransform, uploadedFile]);

  const handleChange = () => {
    const file = inputFileRef.current.files[0];
    let reader = new FileReader();
    const fileDate = file.lastModified;
    const currentTime = new Date().getTime();
    const difference = currentTime - fileDate;
    const oneMinuteInMillis = 60000; // 1 minute in milliseconds
    let source = FILE;

    if (difference < oneMinuteInMillis) {
      source = CAMERA;
    }

    reader.readAsDataURL(file);
    reader.onloadend = function () {
      const base64Data = reader.result;

      const isGreaterThanLimit = file.size > ATTACHMENT_FILE_SIZE_LIMIT_IN_BYTES;

      if (isGreaterThanLimit) {
        const errorMessage = `${t('document_id.file_size_limit_exceeded')} (${ATTACHMENT_FILE_SIZE_LIMIT_IN_MB} MB)`;
        setError(errorMessage);
        inputFileRef.current.value = null;
        return;
      }

      setFile({
        name: file.name,
        size: file.size,
        base64Data,
      });

      onUpdateValue({
        fileinbase64: base64Data,
        position: componentData.response.position,
        source: source
      });
    };
  };

  const handleRemoveFile = () => {
    setFile(null);

    onUpdateValue({
      fileinbase64: null,
      position: componentData.response.position,
      source: null
    });

    inputFileRef.current.value = null;
  }

  const handleUploadFile = () => {
    inputFileRef.current.click();
  }

  const formatFileSize = (sizeInBytes) => {
    if (sizeInBytes > 1048576) return `${(sizeInBytes / 1048576).toFixed(2)} MB`;
    if (sizeInBytes > 1024) return `${(sizeInBytes / 1024).toFixed(2)} KB`;
  }

  const getTitleStyles = () => {
    const { fontFamily, fontSize } = getScreenDimensions();

    const { posx, posy, sizex, page } = componentData.title.position;
    const pageWidth = getPageWidth(page - 1);
    const pageHeight = getPageHeight(page - 1);
    const percentage = calculateWidthPercentageOfPage(page - 1);
    return Helper.getElementPositionStyles(posx, posy, sizex, fontFamily, '', pageWidth, pageHeight, percentage, isHorizontal, fontSize);
  }

  const getResponseStyles = () => {
    const { fontFamily, fontSize } = getScreenDimensions();

    const position = componentData?.response?.position || {};

    const { posx = 0, posy = 0, sizex, page } = position;

    const pageWidth = getPageWidth(page - 1);
    const pageHeight = getPageHeight(page - 1);
    const percentage = calculateWidthPercentageOfPage(page - 1);
    let styles = Helper.getElementPositionStyles(posx, posy, sizex, fontFamily, '', pageWidth, pageHeight, percentage, isHorizontal, fontSize);

    return styles;
  }

  const contentTypeIsImage = () => {
    return contentType.includes('image');
  }

  return (
    <>
      <div className={`${isDisabled ? 'disabled-container' : ''} ${hasError && !isAdvancedForm ? 'error-container' : ''} ${hasError && isAdvancedForm ? 'attachment-container-error' : ''} ${isAdvancedForm ? 'advanced-form' : 'attachment-container'} `}
      >
        {isHTML ? (
          <div style={isAdvancedForm ? getTitleStyles() : {}}
            dangerouslySetInnerHTML={Helper.mapInnerHtmlData(titleValue)}
          />
        ) : (
          <label style={isAdvancedForm ? getTitleStyles() : {}}>{titleValue}</label>
        )}
        {!uploadedFile && (
          <Button
            disabled={isDisabled}
            style={{
              ...(isAdvancedForm ? getTitleStyles() : {}),
              ...(shouldAddButtonMargin ? { marginTop: buttonMarginTop } : {}),
            }}
            variant="success"
            className="file-button"
            onClick={handleUploadFile}
          >
            {t('document_id.upload_file')}
          </Button>
        )}
        <input
          accept={contentType}
          onChange={handleChange}
          type="file"
          ref={inputFileRef}
          id="getFile"
          style={{ display: "none" }}
        />
        {uploadedFile && (
          <>
            {contentTypeIsImage() ? (
              <>
                <img
                  ref={imageRef}
                  className="attachment-img"
                  style={{
                    ...(isAdvancedForm ? getResponseStyles() : {}),
                  }}
                  src={uploadedFile.base64Data}
                  alt=""
                />
                <div style={infoContainerStyle} className={isAdvancedForm ? 'label-attachments' : 'file-info-container'}>
                  <AttachmentIcon />
                  <label>{Helper.truncateText(uploadedFile.name, 10)}</label>
                  <label>{formatFileSize(uploadedFile.size)}</label>
                  <img src={cancelIcon} onClick={handleRemoveFile} alt="cancel" />
                </div>
              </>
            ) : (
              <div style={{
                ...(isAdvancedForm ? getTitleStyles() : {}),
                ...(shouldAddButtonMargin ? { marginTop: buttonMarginTop } : {}),
              }} className={`file-info-container ${isAdvancedForm ? 'label-attachments' : ''}`}>
                <AttachmentIcon />
                <label>{Helper.truncateText(uploadedFile.name, 10)}</label>
                <label> {formatFileSize(uploadedFile.size)}</label>
                <img src={cancelIcon} onClick={handleRemoveFile} alt="cancel" />
              </div>
            )}
          </>
        )}
      </div>
      <ShowErrorModal openShowErrorModal={error} setOpenShowErrorModal={setError} errorMessage={error} isBatchMultisignature={isBatchMultisignature} />
    </>
  );
};
